import { ref } from 'vue';
import axios from 'axios';
import { api } from 'boot/axios';

// Formatos aceptados y limite de tamano. Son videos de clase largos, sin
// transcodificar: se sube el archivo tal cual (a diferencia de el-templo que
// transcodifica clips cortos en el browser).
const ACCEPTED_EXTENSIONS = ['.mp4', '.mov', '.webm', '.m4v'];
const MAX_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB

/** Composable de subida de video de una leccion a R2 (presign -> PUT -> confirm). */
export function useVideoUpload() {
  const uploading = ref(false);
  const progress = ref(0);

  /** Valida extension y tamano. Devuelve un mensaje de error o null si es valido. */
  function validate(file: File): string | null {
    const name = file.name.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
      return 'Formato no soportado. Usá MP4, MOV, WEBM o M4V.';
    }
    if (file.size > MAX_SIZE_BYTES) {
      return 'El archivo supera el límite de 2 GB.';
    }
    return null;
  }

  /**
   * Sube el video: pide presigned URL, hace PUT directo a R2 (con progreso) y
   * confirma. Devuelve la URL publica del video, o null si R2 no devolvio URL.
   */
  async function uploadVideo(lessonId: string, file: File): Promise<string | null> {
    uploading.value = true;
    progress.value = 0;
    try {
      // 1. Presigned URL desde la API
      const { data } = await api.post<{ uploadUrl: string; key: string }>(
        `/admin/lessons/${lessonId}/video/upload-url`,
      );

      // 2. PUT directo a R2 (no pasa por el backend). axios crudo, sin los
      // interceptores de `api` (que agregan Authorization y baseURL).
      await axios.put(data.uploadUrl, file, {
        headers: { 'Content-Type': file.type || 'video/mp4' },
        onUploadProgress: (e) => {
          if (e.total) progress.value = Math.round((e.loaded / e.total) * 100);
        },
      });

      // 3. Confirmar: el backend guarda la URL publica en lessons.videoUrl
      const { data: res } = await api.post<{ success: boolean; videoUrl: string | null }>(
        `/admin/lessons/${lessonId}/video/upload-complete`,
      );
      return res.videoUrl;
    } finally {
      uploading.value = false;
      progress.value = 0;
    }
  }

  /** Elimina el video de la leccion (R2 + DB). */
  async function deleteVideo(lessonId: string): Promise<void> {
    await api.delete(`/admin/lessons/${lessonId}/video`);
  }

  return { uploading, progress, validate, uploadVideo, deleteVideo };
}
